import React, { useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import Avatar from "@/components/chat/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

export default function NewChatDialog({ open, onOpenChange, me, users, onCreated }) {
  const [q, setQ] = useState("");
  const [groupName, setGroupName] = useState("");
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  const others = useMemo(
    () => users.filter((u) => u.id !== me.id && u.active),
    [users, me.id]
  );
  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return others.filter((u) => u.displayName.toLowerCase().includes(s) || u.email.toLowerCase().includes(s));
  }, [others, q]);

  const reset = () => { setQ(""); setGroupName(""); setSelected([]); };

  const createDirect = async (userId) => {
    setLoading(true);
    try {
      const { data } = await api.post("/conversations", { type: "direct", memberIds: [userId] });
      reset();
      onCreated(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const createGroup = async () => {
    if (!groupName.trim()) return toast.error("Inserisci un nome per il gruppo");
    if (selected.length < 1) return toast.error("Seleziona almeno un membro");
    setLoading(true);
    try {
      const { data } = await api.post("/conversations", { type: "group", name: groupName.trim(), memberIds: selected });
      reset();
      onCreated(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="new-chat-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo contatto</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="direct">
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="direct" data-testid="tab-direct">Contatto</TabsTrigger>
            <TabsTrigger value="group" data-testid="tab-group">Gruppo di lavoro</TabsTrigger>
          </TabsList>

          <TabsContent value="direct" className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-10" placeholder="Cerca referente..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="new-direct-search" />
            </div>
            <div className="max-h-72 overflow-y-auto thin-scroll -mx-2 px-2 space-y-1">
              {filtered.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Nessun utente trovato</p>}
              {filtered.map((u) => (
                <button key={u.id} disabled={loading} data-testid={`new-direct-user-${u.id}`}
                  onClick={() => createDirect(u.id)}
                  className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted transition-colors text-left disabled:opacity-50">
                  <Avatar name={u.displayName} online={u.online} size={40} showStatus={false} />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{u.displayName}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                  </div>
                </button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="group" className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
                <Users className="h-5 w-5 text-white" />
              </div>
              <Input placeholder="Nome del gruppo di lavoro" value={groupName} onChange={(e) => setGroupName(e.target.value)} data-testid="new-group-name" />
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-10" placeholder="Cerca membri..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="new-group-search" />
            </div>
            <div className="max-h-56 overflow-y-auto thin-scroll -mx-2 px-2 space-y-1">
              {filtered.map((u) => (
                <label key={u.id} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer" data-testid={`new-group-user-${u.id}`}>
                  <Checkbox checked={selected.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
                  <Avatar name={u.displayName} online={u.online} size={36} showStatus={false} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{u.displayName}</div>
                  </div>
                </label>
              ))}
            </div>
            <Button onClick={createGroup} disabled={loading} className="w-full bg-sky-600 hover:bg-sky-700 text-white" data-testid="create-group-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : `Crea gruppo${selected.length ? ` (${selected.length})` : ""}`}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
